<template>
  <div v-if="modelValue" class="fixed inset-0 z-[10001] overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <!-- Overlay -->
      <div class="fixed inset-0 transition-opacity" @click="$emit('update:modelValue', false)">
        <div class="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"></div>
      </div>
      <!-- Modal -->
      <div class="inline-block align-bottom bg-white dark:bg-gray-800 rounded-2xl text-left overflow-hidden shadow-2xl ring-1 ring-gray-200 dark:ring-gray-700 transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
        <div class="px-6 pt-6 pb-4 sm:px-7 sm:pb-5">
          <h3 class="text-xl md:text-2xl font-semibold mb-5 text-gray-900 dark:text-white">Paramètres du compte</h3>
          <!-- Tabs -->
          <div class="mb-5">
            <nav class="flex gap-2" aria-label="Tabs">
              <button :class="tabClass('profile')" @click="activeTab = 'profile'">Profil</button>
              <button :class="tabClass('security')" @click="activeTab = 'security'">Sécurité</button>
              <button :class="tabClass('danger')" @click="activeTab = 'danger'">Zone dangereuse</button>
            </nav>
          </div>
          <!-- Profile tab -->
          <div v-if="activeTab === 'profile'" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Nom</label>
              <input v-model="form.name" type="text" class="mt-1 block w-full rounded-md bg-white border border-gray-400 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:ring-indigo-500 focus:border-indigo-500 px-3 py-3" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
              <input v-model="form.email" type="email" class="mt-1 block w-full rounded-md bg-white border border-gray-400 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:ring-indigo-500 focus:border-indigo-500 px-3 py-3" />
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Changer l'email déclenchera une reconfirmation et une déconnexion.</p>
            </div>
            <div class="flex justify-end space-x-2 pt-2">
              <button @click="$emit('update:modelValue', false)" class="px-4 py-2 rounded-md text-sm bg-gray-100 hover:bg-gray-200 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200 transition-colors">Annuler</button>
              <button @click="saveProfile" :disabled="loading" class="px-4 py-2 rounded-md text-sm bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50 transition-colors">Enregistrer</button>
            </div>
          </div>
          <!-- Security tab -->
          <div v-if="activeTab === 'security'" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Ancien mot de passe</label>
              <div class="mt-1 relative">
                <input
                  v-model="passwordForm.oldPassword"
                  :type="showOld ? 'text' : 'password'"
                  autocomplete="current-password"
                  class="block w-full rounded-md bg-white border border-gray-400 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:ring-indigo-500 focus:border-indigo-500 px-3 py-3 pr-10"
                />
                <button
                  type="button"
                  @click="showOld = !showOld"
                  :aria-label="showOld ? 'Masquer le mot de passe' : 'Afficher le mot de passe'"
                  class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-200 focus:outline-none"
                >
                  <svg v-if="!showOld" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a21.8 21.8 0 0 1 5.06-6.94"/>
                    <path d="M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a21.8 21.8 0 0 1-3.2 4.6"/>
                    <path d="M14.12 14.12A3 3 0 0 1 9.88 9.88"/>
                    <path d="M1 1l22 22"/>
                  </svg>
                </button>
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Nouveau mot de passe</label>
              <div class="mt-1 relative">
                <input
                  v-model="passwordForm.newPassword"
                  :type="showNew ? 'text' : 'password'"
                  autocomplete="new-password"
                  class="block w-full rounded-md bg-white border border-gray-400 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:ring-indigo-500 focus:border-indigo-500 px-3 py-3 pr-10"
                />
                <button
                  type="button"
                  @click="showNew = !showNew"
                  :aria-label="showNew ? 'Masquer le mot de passe' : 'Afficher le mot de passe'"
                  class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-200 focus:outline-none"
                >
                  <svg v-if="!showNew" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a21.8 21.8 0 0 1 5.06-6.94"/>
                    <path d="M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a21.8 21.8 0 0 1-3.2 4.6"/>
                    <path d="M14.12 14.12A3 3 0 0 1 9.88 9.88"/>
                    <path d="M1 1l22 22"/>
                  </svg>
                </button>
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Confirmer le nouveau mot de passe</label>
              <input v-model="passwordForm.confirm" type="password" autocomplete="new-password" class="mt-1 block w-full rounded-md bg-white border border-gray-400 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:ring-indigo-500 focus:border-indigo-500 px-3 py-3" />
            </div>
            <div class="flex justify-end space-x-2 pt-2">
              <button @click="$emit('update:modelValue', false)" class="px-4 py-2 rounded-md text-sm bg-gray-100 hover:bg-gray-200 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200 transition-colors">Annuler</button>
              <button @click="updatePassword" :disabled="loading" class="px-4 py-2 rounded-md text-sm bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50 transition-colors">Mettre à jour</button>
            </div>
          </div>
          <!-- Danger zone -->
          <div v-if="activeTab === 'danger'" class="space-y-3">
            <p class="text-sm text-gray-600 dark:text-gray-300"><strong>Une fois supprimé, votre compte et l’ensemble de vos données personnelles seront effacés de manière définitive, en conformité avec le RGPD.</strong><br />
Vous gardez ainsi la maîtrise totale de vos informations. Attention : cette action est irréversible.</p>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Tapez SUPPRIMER pour confirmer</label>
              <input v-model="confirmText" type="text" class="mt-1 block w-full rounded-md bg-white border border-gray-400 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm px-3 py-3" placeholder="SUPPRIMER" />
            </div>
            <div class="flex justify-end">
              <button @click="deleteMyAccount" :disabled="confirmText !== 'SUPPRIMER' || loading" class="px-4 py-2 rounded-md text-sm bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 transition-colors">Supprimer mon compte</button>
            </div>
          </div>
          <!-- Confirm Deletion Modal -->
          <div v-if="showConfirm" class="fixed inset-0 z-[10002] flex items-center justify-center">
            <div class="absolute inset-0 bg-gray-900/60" @click="!loading && cancelConfirm"></div>
            <div class="relative mx-4 w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 shadow-2xl ring-1 ring-gray-200 dark:ring-gray-700 p-6">
              <h4 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">Confirmer la suppression</h4>
              <p class="text-sm text-gray-600 dark:text-gray-300 mb-4">Cette action supprimera définitivement votre compte et vos données. Elle est <strong>irréversible</strong>.</p>
              <div class="flex justify-end gap-2">
                <button @click="cancelConfirm" :disabled="loading" class="px-4 py-2 rounded-md text-sm bg-gray-100 hover:bg-gray-200 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200 transition-colors disabled:opacity-50">Annuler</button>
                <button @click="confirmDeletion" :disabled="loading" class="px-4 py-2 rounded-md text-sm bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 transition-colors flex items-center gap-2">
                  <svg v-if="loading" class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                  </svg>
                  <span>{{ loading ? 'Planification…' : 'Confirmer la suppression' }}</span>
                </button>
              </div>
            </div>
          </div>
          <!-- Errors / feedback -->
          <p v-if="error" class="mt-4 text-sm text-red-600">{{ error }}</p>
          <p v-if="info" class="mt-4 text-sm text-green-600">{{ info }}</p>
        </div>
        <div class="bg-gray-50 dark:bg-gray-700 px-5 py-3 sm:px-7 sm:flex sm:flex-row-reverse border-t border-gray-200 dark:border-gray-700">
          <button 
            @click="$emit('update:modelValue', false)" 
            type="button" 
            class="w-full inline-flex justify-center rounded-md border border-transparent shadow px-4 py-2 bg-gray-600 text-base font-medium text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:ml-3 sm:w-auto sm:text-sm"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { toast } from '@/utils/toast'
import { isValidEmail, validatePasswordStrength } from '@/utils/validation'
import { storeToRefs } from 'pinia'
export default defineComponent({
  name: 'AccountSettingsModal',
  props: {
    modelValue: { type: Boolean, required: true }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const router = useRouter()
    const auth = useAuthStore()
    const { user } = storeToRefs(auth)
    // use global toast util
    const activeTab = ref<'profile'|'security'|'danger'>('profile')
    const form = ref({ name: '', email: '' })
    const passwordForm = ref({ oldPassword: '', newPassword: '', confirm: '' })
    const showOld = ref(false)
    const showNew = ref(false)
    const confirmText = ref('')
    const loading = ref(false)
    const error = ref('')
    const info = ref('')
    const showConfirm = ref(false)
    watch(() => props.modelValue, (open) => {
      if (open) {
        // Init form from current user
        if (!user.value) auth.fetchMe()
        form.value.name = user.value?.name || ''
        form.value.email = user.value?.email || ''
        activeTab.value = 'profile'
        passwordForm.value = { oldPassword: '', newPassword: '', confirm: '' }
        confirmText.value = ''
        error.value = ''
        info.value = ''
      }
    }, { immediate: true })
    const tabClass = (tab: 'profile'|'security'|'danger') => {
      const isActive = activeTab.value === tab
      const base = 'px-3 py-1.5 rounded-full text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500/40'
      const active = 'bg-indigo-600 text-white dark:bg-indigo-500 dark:text-white'
      const inactive = 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
      return `${base} ${isActive ? active : inactive}`
    }
    const saveProfile = async () => {
      error.value = ''
      info.value = ''
      loading.value = true
      try {
        const payload: { name?: string; email?: string } = {}
        if (form.value.name !== user.value?.name) payload.name = form.value.name
        if (form.value.email !== user.value?.email) payload.email = form.value.email
        // Email validation if changed
        if (payload.email && !isValidEmail(payload.email)) {
          loading.value = false
          error.value = "Format d'email invalide."
          toast.error(error.value)
          return
        }
        const res = await auth.updateProfile(payload)
        if (res.requiresReauth) {
          info.value = res.message || 'Profil mis à jour. Vous allez être déconnecté.'
          toast.info(info.value)
          emit('update:modelValue', false)
          await router.replace('/login')
        } else {
          info.value = 'Profil mis à jour.'
          toast.success(info.value)
        }
        // Fermer si pas de reauth
        if (!res.requiresReauth) emit('update:modelValue', false)
      } catch (e: unknown) {
        error.value = (e as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error || (e instanceof Error ? e.message : 'Erreur lors de la mise à jour du profil')
        toast.error(error.value)
      } finally {
        loading.value = false
      }
    }
    const updatePassword = async () => {
      error.value = ''
      info.value = ''
      if (!passwordForm.value.oldPassword || !passwordForm.value.newPassword) {
        error.value = 'Veuillez renseigner tous les champs.'
        return
      }
      if (passwordForm.value.newPassword !== passwordForm.value.confirm) {
        error.value = 'Les mots de passe ne correspondent pas.'
        return
      }
      // Strength validation with context (approximate Django policies)
      const ctx = { name: user.value?.name, email: user.value?.email }
      const result = validatePasswordStrength(passwordForm.value.newPassword, ctx)
      if (!result.valid) {
        error.value = result.errors.join(' ')
        toast.error(error.value)
        return
      }
      loading.value = true
      try {
        await auth.changePassword({ oldPassword: passwordForm.value.oldPassword, newPassword: passwordForm.value.newPassword })
        info.value = 'Mot de passe mis à jour. Vous allez être déconnecté.'
        toast.success(info.value)
        // Fermer la modale et rediriger vers la page de connexion (fallback: home)
        emit('update:modelValue', false)
        try {
          await router.push('/login')
        } catch {
          try { await router.push('/') } catch { /* ignored */ }
        }
      } catch (e: unknown) {
        error.value = (e as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error || (e instanceof Error ? e.message : 'Erreur lors de la mise à jour du mot de passe')
        toast.error(error.value)
      } finally {
        loading.value = false
      }
    }
    const deleteMyAccount = () => {
      error.value = ''
      info.value = ''
      if (confirmText.value !== 'SUPPRIMER') return
      showConfirm.value = true
    }
    const cancelConfirm = () => {
      showConfirm.value = false
    }
    const confirmDeletion = async () => {
      loading.value = true
      try {
        await auth.deleteAccount()
        info.value = 'Suppression planifiée. Vous allez être déconnecté.'
        toast.success(info.value)
        showConfirm.value = false
        // Fermer la modale et rediriger vers la page de connexion
        emit('update:modelValue', false)
        try {
          await router.replace('/login')
        } catch {
          try { await router.replace('/') } catch { /* ignored */ }
        }
      } catch (e: unknown) {
        showConfirm.value = false
        const axiosError = e as { response?: { data?: { error?: string; details?: Record<string, number> } }; message?: string }
        const backendMsg = axiosError?.response?.data?.error
        const details = axiosError?.response?.data?.details
        if (backendMsg) {
          let msg = backendMsg
          if (details && typeof details === 'object') {
            const count = Object.values(details).reduce((a: number, v: unknown) => a + (typeof v === 'number' ? v : 0), 0)
            if (count > 0) {
              msg += ' (' + count + ' référence(s))'
            }
          }
          error.value = msg
        } else {
          error.value = axiosError.message || 'Erreur lors de la suppression du compte'
        }
        toast.error(error.value)
      } finally {
        loading.value = false
      }
    }
    return {
      activeTab, form, passwordForm, confirmText,
      loading, error, info, showOld, showNew, showConfirm,
      tabClass, saveProfile, updatePassword, deleteMyAccount, confirmDeletion, cancelConfirm
    }
  }
})
</script>
